/**
 * Phase 0 — `rel_breakup_fresh_pain_phase1` (7-day variant)
 * - Editorial family aligned with `rel_breakup_fresh_pain.reference18.blueprint.js` (same voice system).
 * - Master Spec / progress: INNERVOICE_NORMALIZATION_RUN_PROGRESS.md
 * - Version bump when changing narrative; keep Day 1 as the tone anchor.
 */
import { PHASE1_DURATION_DAYS, PHASE1_PROBLEM_KEY } from "./config";

function gendered(male, female, other = male) {
  return { male, female, other };
}

function localizedLines(heMale, heFemale, en, es) {
  return {
    he: gendered(heMale, heFemale),
    en,
    es,
  };
}

function localizedPrompts(heMale, heFemale, en, es) {
  return {
    he: gendered(heMale, heFemale),
    en,
    es,
  };
}

function continueCta() {
  return {
    primary: {
      label: {
        he: "המשך",
        en: "Continue",
        es: "Continuar",
      },
    },
  };
}

function finishCta() {
  return {
    primary: {
      label: {
        he: "סיימתי להיום",
        en: "I am done for today",
        es: "Termine por hoy",
      },
    },
  };
}

function textStep(id, body, ctas = continueCta()) {
  return {
    id,
    type: "text",
    body,
    ctas,
  };
}

function audioStep(id, body, ctas = continueCta()) {
  return {
    id,
    type: "audio",
    body,
    ctas,
  };
}

function writingStep(id, prompts) {
  return {
    id,
    type: "writing",
    body: {
      he: [],
      en: [],
      es: [],
    },
    prompts,
    ctas: continueCta(),
  };
}

const days = [
  {
    dayNumber: 1,
    emotionalStage: "pause",
    title: {
      he: "לעצור ולשמוע",
      en: "Pause and listen",
      es: "Pausa y escucha",
    },
    titleLine: {
      he: "לעצור ולשמוע",
      en: "Pause and listen",
      es: "Pausa y escucha",
    },
    subtitle: { he: "", en: "", es: "" },
    screens: [
      textStep(
        "d1_opening",
        localizedLines(
          [
            "היום.",
            "אני מתחיל להריץ בראש שלי כל מיני דברים.",
            "המוח שלי כל הזמן עובד.",
            "עובד, עובד שעות נוספות.",
            "האמת היא שאני לא מסתדר.",
            "אני לא מסתדר.",
            "משהו לא מסתדר לי פה.",
            "ואני כל הזמן בורח.",
            "אני לא מבין למה.",
            "למה אני בורח?",
            "אני מנסה לרכז את עצמי בתוך הראש שלי.",
            "לנסות להבין למה אני בורח.",
            "מה קורה איתי.",
            "למה אני לא מסתדר.",
            "כדי לראות מה באמת קורה שם בפנים.",
            "אבל האמת היא שאני לא רוצה לתקן את עצמי עכשיו.",
            "אני גם לא רוצה להבין הכול.",
            "אני גם לא רוצה להוכיח לאף אחד שאני חזק מבחוץ.",
            "אבל אני מרגיש עייף.",
            "עייף מלברוח.",
            "עייף מלהעמיד פנים מול אנשים.",
            "עייף מלהחזיק את הכול עליי לבד.",
            "אז רגע.",
            "אני רוצה לעצור רגע.",
            "לא לברוח.",
            "לא להיעלם.",
            "לא לדחוף שום דבר ממני.",
            "רק להישאר רגע בנקודה הזאת.",
            "כי הכאב הזה שיש בי הוא לא סתם.",
            "זה כאב פנימי.",
            "כאב חזק.",
            "כאב שממש משפיע עליי.",
            "ואני לא רוצה עכשיו לנתח אותו.",
            "לא להסביר אותו.",
            "לא להצדיק אותו.",
            "אני גם לא רוצה להחליט אם הוא חזק או חלש.",
            "אני רק יודע שהוא כאן.",
            "הוא קיים בי.",
            "ואני מרגיש אותו חזק.",
            "מאוד חזק.",
          ],
          [
            "היום.",
            "אני מתחילה להריץ בראש שלי כל מיני דברים.",
            "המוח שלי כל הזמן עובד.",
            "עובד, עובד שעות נוספות.",
            "האמת היא שאני לא מסתדרת.",
            "אני לא מסתדרת.",
            "משהו לא מסתדר לי פה.",
            "ואני כל הזמן בורחת.",
            "אני לא מבינה למה.",
            "למה אני בורחת?",
            "אני מנסה לרכז את עצמי בתוך הראש שלי.",
            "לנסות להבין למה אני בורחת.",
            "מה קורה איתי.",
            "למה אני לא מסתדרת.",
            "כדי לראות מה באמת קורה שם בפנים.",
            "אבל האמת היא שאני לא רוצה לתקן את עצמי עכשיו.",
            "אני גם לא רוצה להבין הכול.",
            "אני גם לא רוצה להוכיח לאף אחד שאני חזקה מבחוץ.",
            "אבל אני מרגישה עייפה.",
            "עייפה מלברוח.",
            "עייפה מלהעמיד פנים מול אנשים.",
            "עייפה מלהחזיק את הכול עליי לבד.",
            "אז רגע.",
            "אני רוצה לעצור רגע.",
            "לא לברוח.",
            "לא להיעלם.",
            "לא לדחוף שום דבר ממני.",
            "רק להישאר רגע בנקודה הזאת.",
            "כי הכאב הזה שיש בי הוא לא סתם.",
            "זה כאב פנימי.",
            "כאב חזק.",
            "כאב שממש משפיע עליי.",
            "ואני לא רוצה עכשיו לנתח אותו.",
            "לא להסביר אותו.",
            "לא להצדיק אותו.",
            "אני גם לא רוצה להחליט אם הוא חזק או חלש.",
            "אני רק יודעת שהוא כאן.",
            "הוא קיים בי.",
            "ואני מרגישה אותו חזק.",
            "מאוד חזק.",
          ],
          [
            "Today.",
            "I start running all kinds of things through my head.",
            "My mind keeps working.",
            "Working, working overtime.",
            "The truth is that I am not managing.",
            "I am not managing.",
            "Something here does not sit right in me.",
            "And I keep running.",
            "I do not understand why.",
            "Why am I running?",
            "I try to gather myself inside my own head.",
            "To try to understand why I am running.",
            "What is happening with me.",
            "Why I am not managing.",
            "To see what is really happening in there.",
            "But the truth is that I do not want to fix myself right now.",
            "I also do not want to understand everything.",
            "I also do not want to prove to anyone that I am strong on the outside.",
            "But I feel tired.",
            "Tired of running.",
            "Tired of pretending in front of people.",
            "Tired of carrying everything on my own.",
            "So for a moment.",
            "I want to pause for a moment.",
            "Not run.",
            "Not disappear.",
            "Not push any part of me away.",
            "Just stay for a moment in this point.",
            "Because this pain in me is not random.",
            "It is an inner pain.",
            "A strong pain.",
            "A pain that truly affects me.",
            "And I do not want to analyze it now.",
            "Not explain it.",
            "Not justify it.",
            "I also do not want to decide whether it is strong or weak.",
            "I only know that it is here.",
            "It exists in me.",
            "And I feel it strongly.",
            "Very strongly.",
          ],
          [
            "Hoy.",
            "Empiezo a pasar por mi cabeza toda clase de cosas.",
            "Mi mente no para de trabajar.",
            "Trabaja y trabaja horas extra.",
            "La verdad es que no me estoy arreglando.",
            "No me estoy arreglando.",
            "Algo aqui no me encaja.",
            "Y todo el tiempo huyo.",
            "No entiendo por que.",
            "Por que estoy huyendo?",
            "Intento reunirme dentro de mi propia cabeza.",
            "Intentar entender por que huyo.",
            "Que me esta pasando.",
            "Por que no me estoy arreglando.",
            "Para ver que es lo que de verdad pasa adentro.",
            "Pero la verdad es que no quiero arreglarme ahora.",
            "Tampoco quiero entenderlo todo.",
            "Tampoco quiero demostrarle a nadie que soy fuerte por fuera.",
            "Pero me siento cansado.",
            "Cansado de huir.",
            "Cansado de fingir frente a la gente.",
            "Cansado de llevarlo todo solo.",
            "Asi que un momento.",
            "Quiero detenerme un momento.",
            "No huir.",
            "No desaparecer.",
            "No empujar ninguna parte de mi lejos.",
            "Solo quedarme un momento en este punto.",
            "Porque este dolor que hay en mi no es cualquier cosa.",
            "Es un dolor interior.",
            "Un dolor fuerte.",
            "Un dolor que de verdad me afecta.",
            "Y ahora no quiero analizarlo.",
            "No explicarlo.",
            "No justificarlo.",
            "Tampoco quiero decidir si es fuerte o debil.",
            "Solo se que esta aqui.",
            "Existe dentro de mi.",
            "Y lo siento fuerte.",
            "Muy fuerte.",
          ]
        )
      ),
      audioStep(
        "d1_mindfulness",
        localizedLines(
          ["רגע.", "אני לא חייב להבין הכול עכשיו.", "אני רק עוצר רגע עם מה שקורה בי."],
          ["רגע.", "אני לא חייבת להבין הכול עכשיו.", "אני רק עוצרת רגע עם מה שקורה בי."],
          ["A moment.", "I do not have to understand everything now.", "I only pause for a moment with what is happening in me."],
          ["Un momento.", "No tengo que entenderlo todo ahora.", "Solo me detengo un momento con lo que esta pasando en mi."]
        )
      ),
      writingStep(
        "d1_writing_1",
        localizedPrompts(
          "ממה אני באמת בורח עכשיו?",
          "ממה אני באמת בורחת עכשיו?",
          "What am I really running from right now?",
          "De que estoy huyendo de verdad ahora?"
        )
      ),
      writingStep(
        "d1_writing_2",
        localizedPrompts(
          "מה הכי קשה לי להרגיש עכשיו?",
          "מה הכי קשה לי להרגיש עכשיו?",
          "What is hardest for me to feel right now?",
          "Que es lo mas dificil para mi sentir ahora?"
        )
      ),
      writingStep(
        "d1_writing_3",
        localizedPrompts(
          "איפה הכאב הזה יושב בי עכשיו?",
          "איפה הכאב הזה יושב בי עכשיו?",
          "Where does this pain sit in me right now?",
          "Donde siento este dolor en mi ahora?"
        )
      ),
      textStep(
        "d1_closing",
        localizedLines(
          ["אני לא חייב להבין הכול היום.", "אולי מספיק שעצרתי רגע."],
          ["אני לא חייבת להבין הכול היום.", "אולי מספיק שעצרתי רגע."],
          ["I do not have to understand everything today.", "Maybe it is enough that I paused for a moment."],
          ["No tengo que entenderlo todo hoy.", "Quizas basta con que me haya detenido un momento."]
        )
      ),
      textStep(
        "d1_action",
        localizedLines(
          ["אני נשאר עוד רגע בשקט לפני שאני ממשיך."],
          ["אני נשארת עוד רגע בשקט לפני שאני ממשיכה."],
          ["I stay one more quiet moment before I continue."],
          ["Me quedo un momento mas en silencio antes de seguir."]
        ),
        finishCta()
      ),
    ],
  },
  {
    dayNumber: 2,
    emotionalStage: "pain",
    title: {
      he: "לתת לכאב קול",
      en: "Give the pain a voice",
      es: "Dar voz al dolor",
    },
    titleLine: {
      he: "לתת לכאב קול",
      en: "Give the pain a voice",
      es: "Dar voz al dolor",
    },
    subtitle: { he: "", en: "", es: "" },
    screens: [
      textStep(
        "d2_opening",
        localizedLines(
          ["היום.", "המחשבה נוגעת, והכאב נפתח בגוף.", "אני לא סוגר אותו מיד."],
          ["היום.", "המחשבה נוגעת, והכאב נפתח בגוף.", "אני לא סוגרת אותו מיד."],
          ["Today.", "The thought touches me, and the pain opens in my body.", "I do not close it at once."],
          ["Hoy.", "El pensamiento me toca, y el dolor se abre en el cuerpo.", "No lo cierro enseguida."]
        )
      ),
      audioStep(
        "d2_mindfulness",
        localizedLines(
          ["אני נותן לכאב לעבור בי לאט.", "אני מרגיש איפה הוא שורף ואיפה הוא מכביד.", "אני לא עוזב את עצמי בתוכו."],
          ["אני נותנת לכאב לעבור בי לאט.", "אני מרגישה איפה הוא שורף ואיפה הוא מכביד.", "אני לא עוזבת את עצמי בתוכו."],
          ["I let the pain move through me slowly.", "I feel where it burns and where it weighs me down.", "I do not leave myself inside it."],
          ["Dejo que el dolor pase por mi despacio.", "Siento donde quema y donde pesa.", "No me dejo solo dentro de el."]
        )
      ),
      writingStep(
        "d2_writing_1",
        localizedPrompts(
          "איזו מילה הכי כואבת בי היום?",
          "איזו מילה הכי כואבת בי היום?",
          "What word hurts the most in me today?",
          "Que palabra duele mas en mi hoy?"
        )
      ),
      writingStep(
        "d2_writing_2",
        localizedPrompts(
          "איפה הגוף שלי מחזיק את זה?",
          "איפה הגוף שלי מחזיק את זה?",
          "Where is my body holding this?",
          "Donde sostiene mi cuerpo esto?"
        )
      ),
      writingStep(
        "d2_writing_3",
        localizedPrompts(
          "מה הלב שלי לא אומר בקול?",
          "מה הלב שלי לא אומר בקול?",
          "What is my heart not saying out loud?",
          "Que no esta diciendo mi corazon en voz alta?"
        )
      ),
      textStep(
        "d2_closing",
        localizedLines(
          ["נתתי לכאב מקום קטן.", "זה מספיק להיום."],
          ["נתתי לכאב מקום קטן.", "זה מספיק להיום."],
          ["I gave the pain a small place.", "That is enough for today."],
          ["Le di al dolor un pequeno lugar.", "Eso basta por hoy."]
        )
      ),
      textStep(
        "d2_action",
        localizedLines(
          ["היום אני שותה מים לאט ושם לב מה הגוף מבקש."],
          ["היום אני שותה מים לאט ושמה לב מה הגוף מבקש."],
          ["Today I drink water slowly and notice what my body is asking for."],
          ["Hoy tomo agua despacio y noto lo que mi cuerpo pide."]
        ),
        finishCta()
      ),
    ],
  },
  {
    dayNumber: 3,
    emotionalStage: "pattern",
    title: {
      he: "לראות את המסלול",
      en: "See the pattern",
      es: "Ver el recorrido",
    },
    titleLine: {
      he: "לראות את המסלול",
      en: "See the pattern",
      es: "Ver el recorrido",
    },
    subtitle: { he: "", en: "", es: "" },
    screens: [
      textStep(
        "d3_opening",
        localizedLines(
          ["היום.", "אותה מחשבה חוזרת, ואותו מסלול נפתח בי.", "אני מסתכל עליו מבפנים."],
          ["היום.", "אותה מחשבה חוזרת, ואותו מסלול נפתח בי.", "אני מסתכלת עליו מבפנים."],
          ["Today.", "The same thought returns, and the same path opens in me.", "I look at it from within."],
          ["Hoy.", "Vuelve el mismo pensamiento, y se abre en mi el mismo recorrido.", "Lo miro desde dentro."]
        )
      ),
      audioStep(
        "d3_mindfulness",
        localizedLines(
          ["אני רואה איך הראש מתחיל להריץ.", "אני מרגיש איך הלב נסגר לפני שאני שם לב.", "עכשיו אני רואה את זה קורה."],
          ["אני רואה איך הראש מתחיל להריץ.", "אני מרגישה איך הלב נסגר לפני שאני שמה לב.", "עכשיו אני רואה את זה קורה."],
          ["I see how my head begins to run the movie.", "I feel how my heart closes before I notice it.", "Now I can see it happening."],
          ["Veo como mi cabeza empieza a correr la pelicula.", "Siento como mi corazon se cierra antes de que me de cuenta.", "Ahora puedo verlo pasar."]
        )
      ),
      writingStep(
        "d3_writing_1",
        localizedPrompts(
          "מתי זה מתחיל בי שוב?",
          "מתי זה מתחיל בי שוב?",
          "When does this start in me again?",
          "Cuando vuelve a empezar esto en mi?"
        )
      ),
      writingStep(
        "d3_writing_2",
        localizedPrompts(
          "מה קורה בגוף שלי רגע לפני?",
          "מה קורה בגוף שלי רגע לפני?",
          "What happens in my body just before it?",
          "Que pasa en mi cuerpo justo antes?"
        )
      ),
      writingStep(
        "d3_writing_3",
        localizedPrompts(
          "לאן אני הולך בפנים כשזה קורה?",
          "לאן אני הולכת בפנים כשזה קורה?",
          "Where do I go inside when this happens?",
          "Adonde voy por dentro cuando esto pasa?"
        )
      ),
      textStep(
        "d3_closing",
        localizedLines(
          ["ראיתי את המסלול הישן.", "אני נשאר איתו עוד רגע."],
          ["ראיתי את המסלול הישן.", "אני נשארת איתו עוד רגע."],
          ["I saw the old path.", "I stay with it a little longer."],
          ["Vi el viejo recorrido.", "Me quedo con el un momento mas."]
        )
      ),
      textStep(
        "d3_action",
        localizedLines(
          ["היום אני שם לב לרגע אחד שבו הסרט מתחיל בי."],
          ["היום אני שמה לב לרגע אחד שבו הסרט מתחיל בי."],
          ["Today I notice one moment when the movie starts in me."],
          ["Hoy noto un momento en el que la pelicula empieza en mi."]
        ),
        finishCta()
      ),
    ],
  },
  {
    dayNumber: 4,
    emotionalStage: "fear",
    title: {
      he: "לפגוש את הפחד",
      en: "Meet the fear",
      es: "Encontrar el miedo",
    },
    titleLine: {
      he: "לפגוש את הפחד",
      en: "Meet the fear",
      es: "Encontrar el miedo",
    },
    subtitle: { he: "", en: "", es: "" },
    screens: [
      textStep(
        "d4_opening",
        localizedLines(
          ["היום.", "מתחת למחשבה יש פחד שמחזיק אותה.", "אני מתקרב אליו לאט."],
          ["היום.", "מתחת למחשבה יש פחד שמחזיק אותה.", "אני מתקרבת אליו לאט."],
          ["Today.", "Under the thought there is a fear holding it up.", "I move closer to it slowly."],
          ["Hoy.", "Debajo del pensamiento hay un miedo que lo sostiene.", "Me acerco a el despacio."]
        )
      ),
      audioStep(
        "d4_mindfulness",
        localizedLines(
          ["אני שומע את המשפט הקשה שעולה בי.", "אני מרגיש את הדופק איתו.", "אני נשאר מולו עוד רגע."],
          ["אני שומעת את המשפט הקשה שעולה בי.", "אני מרגישה את הדופק איתו.", "אני נשארת מולו עוד רגע."],
          ["I hear the hard sentence rising in me.", "I feel my pulse with it.", "I stay in front of it a little longer."],
          ["Escucho la frase dura que sube en mi.", "Siento el pulso con ella.", "Me quedo frente a ella un poco mas."]
        )
      ),
      writingStep(
        "d4_writing_1",
        localizedPrompts(
          "מה אני הכי מפחד שיישאר איתי?",
          "מה אני הכי מפחדת שיישאר איתי?",
          "What am I most afraid will stay with me?",
          "Que es lo que mas temo que se quede conmigo?"
        )
      ),
      writingStep(
        "d4_writing_2",
        localizedPrompts(
          "מה המחשבה הזאת אומרת עליי?",
          "מה המחשבה הזאת אומרת עליי?",
          "What does this thought say about me?",
          "Que dice este pensamiento sobre mi?"
        )
      ),
      writingStep(
        "d4_writing_3",
        localizedPrompts(
          "מה החלק שבי מבקש לשמוע עכשיו?",
          "מה החלק שבי מבקש לשמוע עכשיו?",
          "What is the part in me asking to hear right now?",
          "Que parte de mi pide escuchar algo ahora?"
        )
      ),
      textStep(
        "d4_closing",
        localizedLines(
          ["פגשתי את הפחד קצת.", "אני לא בורח ממנו עכשיו."],
          ["פגשתי את הפחד קצת.", "אני לא בורחת ממנו עכשיו."],
          ["I met the fear a little.", "I am not running from it now."],
          ["Me encontre un poco con el miedo.", "No huyo de el ahora."]
        )
      ),
      textStep(
        "d4_action",
        localizedLines(
          ["היום אני אומר לעצמי משפט אחד רך כשפחד עולה."],
          ["היום אני אומרת לעצמי משפט אחד רך כשפחד עולה."],
          ["Today I say one soft sentence to myself when fear rises."],
          ["Hoy me digo una frase suave cuando sube el miedo."]
        ),
        finishCta()
      ),
    ],
  },
  {
    dayNumber: 5,
    emotionalStage: "need",
    title: {
      he: "לשמוע מה חסר בי",
      en: "Hear what is missing in me",
      es: "Escuchar lo que me falta",
    },
    titleLine: {
      he: "לשמוע מה חסר בי",
      en: "Hear what is missing in me",
      es: "Escuchar lo que me falta",
    },
    subtitle: { he: "", en: "", es: "" },
    screens: [
      textStep(
        "d5_opening",
        localizedLines(
          ["היום.", "מתחת לפחד יש צורך שלא קיבל מקום.", "אני מקשיב לו בלי בושה."],
          ["היום.", "מתחת לפחד יש צורך שלא קיבל מקום.", "אני מקשיבה לו בלי בושה."],
          ["Today.", "Under the fear there is a need that did not get space.", "I listen to it without shame."],
          ["Hoy.", "Debajo del miedo hay una necesidad que no recibio lugar.", "La escucho sin verguenza."]
        )
      ),
      audioStep(
        "d5_mindfulness",
        localizedLines(
          ["אני שומע את החלק בי שמתכווץ.", "אני מרגיש כמה הוא מחכה למשהו פשוט.", "אני לא משתיק אותו."],
          ["אני שומעת את החלק בי שמתכווץ.", "אני מרגישה כמה הוא מחכה למשהו פשוט.", "אני לא משתיקה אותו."],
          ["I hear the part in me that tightens.", "I feel how much it is waiting for something simple.", "I do not silence it."],
          ["Escucho la parte de mi que se encoge.", "Siento cuanto espera algo simple.", "No la hago callar."]
        )
      ),
      writingStep(
        "d5_writing_1",
        localizedPrompts(
          "מה הכי חסר לי עכשיו?",
          "מה הכי חסר לי עכשיו?",
          "What is missing most for me right now?",
          "Que es lo que mas me falta ahora?"
        )
      ),
      writingStep(
        "d5_writing_2",
        localizedPrompts(
          "לאיזה חיבוק פנימי אני מתגעגע?",
          "לאיזה חיבוק פנימי אני מתגעגעת?",
          "What inner embrace am I longing for?",
          "A que abrazo interior extrano?"
        )
      ),
      writingStep(
        "d5_writing_3",
        localizedPrompts(
          "מה אני רוצה לתת לעצמי היום?",
          "מה אני רוצה לתת לעצמי היום?",
          "What do I want to give myself today?",
          "Que quiero darme hoy?"
        )
      ),
      textStep(
        "d5_closing",
        localizedLines(
          ["יש בי צורך אמיתי.", "אני נותן לו מקום קטן."],
          ["יש בי צורך אמיתי.", "אני נותנת לו מקום קטן."],
          ["There is a real need in me.", "I give it a small place."],
          ["Hay una necesidad real en mi.", "Le doy un pequeno lugar."]
        )
      ),
      textStep(
        "d5_action",
        localizedLines(
          ["היום אני נותן לעצמי דבר קטן שמרגיש כמו מקום."],
          ["היום אני נותנת לעצמי דבר קטן שמרגיש כמו מקום."],
          ["Today I give myself one small thing that feels like space."],
          ["Hoy me doy una cosa pequena que se siente como un lugar."]
        ),
        finishCta()
      ),
    ],
  },
  {
    dayNumber: 6,
    emotionalStage: "softening",
    title: {
      he: "לתת לקול הרך מקום",
      en: "Give the softer voice space",
      es: "Dar lugar a la voz suave",
    },
    titleLine: {
      he: "לתת לקול הרך מקום",
      en: "Give the softer voice space",
      es: "Dar lugar a la voz suave",
    },
    subtitle: { he: "", en: "", es: "" },
    screens: [
      textStep(
        "d6_opening",
        localizedLines(
          ["היום.", "אני שומע בתוכי קול קצת יותר יציב.", "הוא לא מוחק את הכאב. הוא נשאר איתי בתוכו."],
          ["היום.", "אני שומעת בתוכי קול קצת יותר יציב.", "הוא לא מוחק את הכאב. הוא נשאר איתי בתוכו."],
          ["Today.", "I hear a slightly steadier voice inside me.", "It does not erase the pain. It stays with me inside it."],
          ["Hoy.", "Escucho dentro de mi una voz un poco mas firme.", "No borra el dolor. Se queda conmigo dentro de el."]
        )
      ),
      audioStep(
        "d6_mindfulness",
        localizedLines(
          ["אני מרגיש איך הגוף לא נאטם מיד.", "הלב עוד רגיש, אבל הוא כבר לא לבד.", "אני נותן לקול הזה עוד מקום."],
          ["אני מרגישה איך הגוף לא נאטם מיד.", "הלב עוד רגיש, אבל הוא כבר לא לבד.", "אני נותנת לקול הזה עוד מקום."],
          ["I feel how my body does not shut down at once.", "My heart is still tender, but it is no longer alone.", "I give this voice a little more room."],
          ["Siento como mi cuerpo no se cierra enseguida.", "Mi corazon sigue sensible, pero ya no esta solo.", "Le doy a esta voz un poco mas de lugar."]
        )
      ),
      writingStep(
        "d6_writing_1",
        localizedPrompts(
          "איזה משפט פנימי מחזיק אותי היום?",
          "איזה משפט פנימי מחזיק אותי היום?",
          "What inner sentence is holding me today?",
          "Que frase interior me sostiene hoy?"
        )
      ),
      writingStep(
        "d6_writing_2",
        localizedPrompts(
          "איפה הגוף שלי נרגע קצת?",
          "איפה הגוף שלי נרגע קצת?",
          "Where does my body relax a little?",
          "Donde se relaja un poco mi cuerpo?"
        )
      ),
      writingStep(
        "d6_writing_3",
        localizedPrompts(
          "מה אני מגלה עליי כשאני איתי?",
          "מה אני מגלה עליי כשאני איתי?",
          "What do I discover about myself when I am with myself?",
          "Que descubro de mi cuando estoy conmigo?"
        )
      ),
      textStep(
        "d6_closing",
        localizedLines(
          ["משהו בי התרכך קצת.", "אני שומע את זה."],
          ["משהו בי התרכך קצת.", "אני שומעת את זה."],
          ["Something in me softened a little.", "I can hear it."],
          ["Algo en mi se ablando un poco.", "Puedo oirlo."]
        )
      ),
      textStep(
        "d6_action",
        localizedLines(
          ["היום אני הולך כמה דקות בקצב של הנשימה שלי."],
          ["היום אני הולכת כמה דקות בקצב של הנשימה שלי."],
          ["Today I walk for a few minutes at the pace of my breath."],
          ["Hoy camino unos minutos al ritmo de mi respiracion."]
        ),
        finishCta()
      ),
    ],
  },
  {
    dayNumber: 7,
    emotionalStage: "continuation",
    title: {
      he: "להמשיך איתי",
      en: "Continue with myself",
      es: "Seguir conmigo",
    },
    titleLine: {
      he: "להמשיך איתי",
      en: "Continue with myself",
      es: "Seguir conmigo",
    },
    subtitle: { he: "", en: "", es: "" },
    screens: [
      textStep(
        "d7_opening",
        localizedLines(
          ["היום.", "אני לא מסיים סיפור נקי ומסודר.", "אני רק לוקח איתי דרך להיות איתי."],
          ["היום.", "אני לא מסיימת סיפור נקי ומסודר.", "אני רק לוקחת איתי דרך להיות איתי."],
          ["Today.", "I am not finishing a clean and tidy story.", "I am only taking with me a way to be with myself."],
          ["Hoy.", "No termino una historia limpia y ordenada.", "Solo me llevo una manera de estar conmigo."]
        )
      ),
      audioStep(
        "d7_mindfulness",
        localizedLines(
          ["המחשבות עוד יבואו.", "גם הלב עוד יגיב.", "אבל יש בי עכשיו מישהו שנשאר קרוב אליי."],
          ["המחשבות עוד יבואו.", "גם הלב עוד יגיב.", "אבל יש בי עכשיו מישהי שנשארת קרובה אליי."],
          ["The thoughts will still come.", "My heart will still react too.", "But there is someone in me now who stays close to me."],
          ["Los pensamientos todavia vendran.", "Mi corazon tambien seguira reaccionando.", "Pero ahora hay alguien en mi que se queda cerca de mi."]
        )
      ),
      writingStep(
        "d7_writing_1",
        localizedPrompts(
          "מה אני רוצה לשמוע מעצמי גם מחר?",
          "מה אני רוצה לשמוע מעצמי גם מחר?",
          "What do I want to hear from myself tomorrow too?",
          "Que quiero oir de mi tambien manana?"
        )
      ),
      writingStep(
        "d7_writing_2",
        localizedPrompts(
          "איך אני רוצה לפגוש את הלב שלי כשהוא שוב יתכווץ?",
          "איך אני רוצה לפגוש את הלב שלי כשהוא שוב יתכווץ?",
          "How do I want to meet my heart when it tightens again?",
          "Como quiero encontrar a mi corazon cuando vuelva a encogerse?"
        )
      ),
      writingStep(
        "d7_writing_3",
        localizedPrompts(
          "מה נשאר איתי מהשבוע הזה?",
          "מה נשאר איתי מהשבוע הזה?",
          "What stays with me from this week?",
          "Que se queda conmigo de esta semana?"
        )
      ),
      textStep(
        "d7_closing",
        localizedLines(
          ["אני נשאר עם מה שפגשתי השבוע.", "זה הולך איתי הלאה."],
          ["אני נשארת עם מה שפגשתי השבוע.", "זה הולך איתי הלאה."],
          ["I stay with what I met this week.", "It goes with me from here."],
          ["Me quedo con lo que encontre esta semana.", "Se va conmigo desde aqui."]
        )
      ),
      textStep(
        "d7_action",
        localizedLines(
          ["היום אני שומר לעצמי רגע שקט אחד ששייך רק לי."],
          ["היום אני שומרת לעצמי רגע שקט אחד ששייך רק לי."],
          ["Today I keep one quiet moment for myself that belongs only to me."],
          ["Hoy guardo para mi un momento de silencio que me pertenece solo a mi."]
        ),
        finishCta()
      ),
    ],
  },
];

export const freshBreakupPainPhaseOneBlueprint = {
  id: "rel_breakup_fresh_pain_7d",
  version: "phase1_v4",
  problemKey: PHASE1_PROBLEM_KEY,
  journeyKey: PHASE1_PROBLEM_KEY,
  category: "relationships",
  durationDays: PHASE1_DURATION_DAYS,
  totalDays: PHASE1_DURATION_DAYS,
  phase1: true,
  journeyName: {
    he: "כאב פרידה טרייה",
    en: "fresh breakup pain",
    es: "dolor de una ruptura reciente",
  },
  ui: {
    phaseOneMode: true,
    showStageIndicators: false,
    showProgressText: false,
    allowSkipAlways: false,
    allowBack: true,
    hideDayCount: true,
    entryLabel: {
      he: "היום",
      en: "Today",
      es: "Hoy",
    },
    primaryCtaDefault: {
      he: "המשך",
      en: "Continue",
      es: "Continuar",
    },
    secondaryCtaDefault: {
      he: "",
      en: "",
      es: "",
    },
  },
  days,
  languages: {
    he: { days },
    en: { days },
    es: { days },
  },
};

export default freshBreakupPainPhaseOneBlueprint;
